import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { find, isEmpty, map } from 'es-toolkit/compat';
import React from 'react';

import { Permission, PermissionOption, permissionToName } from 'interfaces/permissionOption';
import { usePermissions } from 'utils/usePermissions';

export const CurrentUserPermissionsAccordion: React.FC<
  React.PropsWithChildren<{
    permissions: PermissionOption[];
  }>
> = ({ permissions }) => {
  const {
    hasSomePermissions,
    permissions: currentUserCredentials,
    isLoading: loadingCurrentUserPermissions,
  } = usePermissions();

  const [expandedState, setExpanded] = React.useState<boolean | null>(null);
  // If the user has no permissions to manage permissions, the accordion should be expanded by default
  const expanded =
    expandedState ??
    (!loadingCurrentUserPermissions &&
      !hasSomePermissions([Permission.DefineRolesToLabUsers, Permission.ManageNucleaiUsers]));

  const permissionCount = currentUserCredentials?.length ?? 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, newState) => setExpanded(newState)}
      square
      variant="outlined"
      disabled={loadingCurrentUserPermissions}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Current User Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review the permissions currently granted to your signed-in account.
            </Typography>
          </Box>
          <Chip label={`${permissionCount} active`} size="small" variant="outlined" />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loadingCurrentUserPermissions ? (
          <Typography variant="body2" color="text.secondary">
            Loading current user permissions...
          </Typography>
        ) : currentUserCredentials && !isEmpty(currentUserCredentials) ? (
          <List dense disablePadding>
            {map(currentUserCredentials, (permissionId) => {
              const permissionOption = find(permissions, {
                permission: permissionId,
              });
              return permissionOption ? (
                <ListItem key={permissionId} disableGutters>
                  <ListItemText primary={permissionToName(permissionOption, permissionId)} />
                </ListItem>
              ) : null;
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No permissions set for current user.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
